import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const AutenticacaoDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Sistema de Autenticação no SmartEntrega</Title>

      <Paragraph>
        Este documento explica como funciona o sistema de autenticação no
        projeto SmartEntrega, incluindo o processo de login, gerenciamento de
        sessão e proteção de rotas.
      </Paragraph>

      <SubTitle>Visão Geral</SubTitle>
      <Paragraph>
        O SmartEntrega utiliza um sistema de autenticação baseado em token,
        onde:
      </Paragraph>
      <OrderedList>
        <OrderedListItem>
          O usuário fornece suas credenciais (email, senha e empresa)
        </OrderedListItem>
        <OrderedListItem>
          O servidor valida as credenciais e retorna um token de acesso
        </OrderedListItem>
        <OrderedListItem>
          O token é armazenado no localStorage do navegador
        </OrderedListItem>
        <OrderedListItem>
          O token é incluído em todas as requisições subsequentes
        </OrderedListItem>
        <OrderedListItem>
          Rotas protegidas verificam a existência e validade do token
        </OrderedListItem>
      </OrderedList>

      <SubTitle>Gerenciamento de Estado de Autenticação</SubTitle>
      <Paragraph>
        O estado de autenticação é gerenciado pelo Redux através do slice{' '}
        <Code>authSlice.ts</Code>:
      </Paragraph>
      <CodeSnippet
        code={`// src/store/slices/authSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://aws.adasistemas.com.br/entregas';

// Função para carregar o usuário do localStorage
const loadUserFromLocalStorage = () => {
  try {
    const userData = localStorage.getItem('userData');
    if (userData) {
      return JSON.parse(userData);
    }
    return null;
  } catch (error) {
    console.error('Erro ao carregar usuário do localStorage:', error);
    return null;
  }
};

interface AuthState {
  user: any | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  resetPasswordSuccess: boolean;
}

const initialState: AuthState = {
  user: loadUserFromLocalStorage(),
  token: localStorage.getItem('acessToken'),
  isAuthenticated: !!localStorage.getItem('acessToken'),
  isLoading: false,
  error: null,
  resetPasswordSuccess: false,
};`}
      />

      <SubTitle>Processo de Login</SubTitle>
      <Paragraph>
        O processo de login é implementado como uma thunk action no Redux:
      </Paragraph>
      <CodeSnippet
        code={`export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, empresa }: { email: string; password: string; empresa: string }, { rejectWithValue }) => {
    try {
      const myHeaders = new Headers();
      myHeaders.append("app", "17d25d4e32a8eb91d897358fc01ddd7c");
      myHeaders.append("Content-Type", "application/json");

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({
          empresa: empresa.toLowerCase(),
          login: email.toLowerCase(),
          senha: password,
          idDevice: "31f08efb6430358e",
          modelDevice: "moto g(7)"
        }),
        redirect: "follow" as RequestRedirect
      };

      const response = await fetch(\`\${API_URL}/credenciais/autenticacao\`, requestOptions);
      
      if (!response.ok) {
        throw new Error('Falha na autenticação');
      }

      const accessToken = response.headers.get('access');
      const data = await response.json();
      
      if (!data) {
        throw new Error('Falha na autenticação');
      }
      
      if (accessToken) {
        localStorage.setItem('acessToken', accessToken);
      }
      if(empresa){
        localStorage.setItem('empresa', empresa);
      }
      
      // Salvar os dados do usuário no localStorage
      localStorage.setItem('userData', JSON.stringify(data));
      
      return {
        user: data,
        token: accessToken
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao fazer login. Verifique suas credenciais.');
    }
  }
);`}
      />

      <SubTitle>Redefinição de Senha</SubTitle>
      <Paragraph>
        O sistema também suporta a funcionalidade de redefinição de senha:
      </Paragraph>
      <CodeSnippet
        code={`export const trocarSenha = createAsyncThunk(
  'auth/trocarSenha',
  async (
    { email, empresa }:
    { email: string; empresa: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.auth.trocarSenha(empresa, email);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao solicitar redefinição de senha.');
    }
  }
);`}
      />

      <SubTitle>Processo de Logout</SubTitle>
      <Paragraph>
        O logout é implementado como uma thunk action que remove os dados do
        usuário do localStorage:
      </Paragraph>
      <CodeSnippet
        code={`export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('acessToken');
  localStorage.removeItem('userData');
  return null;
});`}
      />

      <SubTitle>Carregamento de Dados do Usuário</SubTitle>
      <Paragraph>
        O sistema carrega automaticamente os dados do usuário do localStorage
        quando a aplicação é iniciada:
      </Paragraph>
      <CodeSnippet
        code={`export const updateUserFromLocalStorage = createAsyncThunk(
  'auth/updateUserFromLocalStorage',
  async () => {
    return loadUserFromLocalStorage();
  }
);

// No componente App.tsx
useEffect(() => {
  store.dispatch(loadUserData());
}, []);`}
      />

      <SubTitle>Reducers e Actions</SubTitle>
      <Paragraph>
        O slice de autenticação define reducers para gerenciar o estado:
      </Paragraph>
      <CodeSnippet
        code={`const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearResetPasswordSuccess: (state) => {
      state.resetPasswordSuccess = false;
    },
    loadUserData: (state) => {
      const userData = loadUserFromLocalStorage();
      if (userData) {
        state.user = userData;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Trocar Senha
      .addCase(trocarSenha.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(trocarSenha.fulfilled, (state) => {
        state.isLoading = false;
        state.resetPasswordSuccess = true;
      })
      .addCase(trocarSenha.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(updateUserFromLocalStorage.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
        }
      });
  },
});`}
      />

      <SubTitle>Componente de Login</SubTitle>
      <Paragraph>
        O componente de login utiliza o estado e as actions do Redux para
        autenticar o usuário:
      </Paragraph>
      <CodeSnippet
        code={`// src/pages/Login.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../store/slices/authSlice';
import { RootState, AppDispatch } from '../store';
import styled from 'styled-components';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [empresa, setEmpresa] = useState('');
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  
  const { isAuthenticated, isLoading, error } = useSelector(
    (state: RootState) => state.auth
  );
  
  useEffect(() => {
    // Limpar erros anteriores
    dispatch(clearError());
    
    // Redirecionar se já estiver autenticado
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate, dispatch]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !empresa) {
      return;
    }
    
    await dispatch(login({ email, password, empresa }));
  };
  
  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <h2>Login - SmartEntrega</h2>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <FormGroup>
          <label htmlFor="empresa">Empresa</label>
          <input
            type="text"
            id="empresa"
            value={empresa}
            onChange={(e) => setEmpresa(e.target.value)}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <label htmlFor="password">Senha</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </FormGroup>
        
        <LoginButton type="submit" disabled={isLoading}>
          {isLoading ? 'Entrando...' : 'Entrar'}
        </LoginButton>
      </LoginForm>
    </LoginContainer>
  );
};

// Estilos omitidos para brevidade

export default Login;`}
      />

      <SubTitle>Proteção de Rotas</SubTitle>
      <Paragraph>
        Como visto no documento sobre rotas, o SmartEntrega utiliza componentes
        especiais para proteger rotas que requerem autenticação:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/ProtectedRoute.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../store';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

export default ProtectedRoute;`}
      />

      <SubTitle>Configuração da API com Token de Autenticação</SubTitle>
      <Paragraph>
        Para incluir o token de autenticação em todas as requisições, configure
        o cliente de API:
      </Paragraph>
      <CodeSnippet
        code={`// src/services/api.ts
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://aws.adasistemas.com.br/entregas';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'app': '17d25d4e32a8eb91d897358fc01ddd7c',
  },
});

// Interceptor para adicionar o token de autenticação
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('acessToken');
    if (token) {
      config.headers.Authorization = \`Bearer \${token}\`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratar erros de autenticação
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('acessToken');
      localStorage.removeItem('userData');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Métodos de autenticação
  auth: {
    login: (empresa: string, email: string, password: string) => {
      return apiClient.post('/credenciais/autenticacao', {
        empresa: empresa.toLowerCase(),
        login: email.toLowerCase(),
        senha: password,
        idDevice: "31f08efb6430358e",
        modelDevice: "moto g(7)"
      });
    },
    trocarSenha: (empresa: string, email: string, idDevice: string, modelDevice: string) => {
      return apiClient.post('/credenciais/trocarSenha', {
        empresa: empresa.toLowerCase(),
        login: email.toLowerCase(),
        idDevice,
        modelDevice
      });
    },
  },
  
  // Outros métodos da API...
};`}
      />

      <SubTitle>Verificação de Permissões</SubTitle>
      <Paragraph>
        Para verificar se um usuário tem permissões específicas, você pode criar
        funções utilitárias:
      </Paragraph>
      <CodeSnippet
        code={`// src/utils/permissions.ts
import { User } from '../types';

export const isAdmin = (user: User | null): boolean => {
  return user?.codigoGrupoUsuario === 1;
};

export const canAccessReports = (user: User | null): boolean => {
  // Exemplo: usuários com códigos de grupo 1 ou 2 podem acessar relatórios
  return user?.codigoGrupoUsuario === 1 || user?.codigoGrupoUsuario === 2;
};

// Outras verificações de permissão...`}
      />

      <SubTitle>Conclusão</SubTitle>
      <Paragraph>
        O sistema de autenticação no SmartEntrega utiliza Redux para gerenciar o
        estado de autenticação, localStorage para persistir a sessão do usuário,
        e componentes de proteção de rotas para garantir que apenas usuários
        autorizados possam acessar determinadas funcionalidades. O token de
        autenticação é incluído automaticamente em todas as requisições à API, e
        há tratamento para casos de token expirado ou inválido.
      </Paragraph>
    </DocContainer>
  );
};

export default AutenticacaoDoc;
